import type { FC, KeyboardEvent, PropsWithChildren } from "react";
import { useMemo } from "react";

import classNames from "classnames";
import Image from "next/image";

import { useConfig } from "@jti/app-context/useConfig";
import { StoreDefaultIcon } from "@jti/brand-icons/store-default";
import { Button } from "@jti/core-components/components/Button";
import type { FlotiqStore } from "@jti/fe-common";

import { GOOGLE_DIRECTION_URL_CONST, useStoreLocatorContext } from "../../hooks";
import { useLocatorTranslations } from "../../hooks/useLocatorTranslations";
import { getYandexDirectionUrl } from "../../hooks/useYandexMapsStoreLocator/utils";
import { getNaverDirectionUrl } from "../../utils";
import { EventDate } from "../EventDate";

import { StoreLocatorAvailableProducts, StoreLocatorGetDirection, StoreLocatorShopSchedule } from "./components";

import styles from "./StoreLocatorCard.module.scss";

type StoreLocatorCardProps = {
  store: FlotiqStore;
  className?: string;
  isActive: boolean;
  onSelectCard?: (storeId: string, storeName: string, storetype: string) => void;
  onGetDirectionClick?: () => void;
  isListView?: boolean;
  storeDetailsPageUrl?: string;
  ctaLabel?: string;
  displayImage?: boolean;
  fillHeight?: boolean;
};

type StoreWithEventDate = FlotiqStore & {
  StartDate?: string;
  EndDate?: string;
};

export const StoreLocatorCard: FC<PropsWithChildren<StoreLocatorCardProps>> = ({
  store,
  className,
  isActive,
  onSelectCard,
  children,
  isListView,
  onGetDirectionClick,
  storeDetailsPageUrl,
  ctaLabel,
  displayImage = false,
  fillHeight = false,
}) => {
  const {
    isOpeningHoursEnabled,
    isAvailableDevicesEnabled,
    isAvailableSticksEnabled,
    storeDetailsPageTarget,
    translationPrefix,
  } = useStoreLocatorContext();
  const { __ } = useLocatorTranslations();
  const { locationServiceMapProvider } = useConfig();

  const isOpeningHoursBlockVisible = isOpeningHoursEnabled && !!store.opening_hours?.length;
  const isAvailableProductsVisible =
    (isAvailableDevicesEnabled || isAvailableSticksEnabled) && !!store.product_attributes?.length;
  const isEventCard = translationPrefix === "events";
  const eventStore = store as StoreWithEventDate;

  const directionURL = useMemo(() => {
    switch (locationServiceMapProvider) {
      case "google":
        return `${GOOGLE_DIRECTION_URL_CONST}${store.Location.lat},${store.Location.lon}`;
      case "naver":
        return getNaverDirectionUrl(store.ExternalId ?? "");
      case "yandex":
        return getYandexDirectionUrl(store);
      default:
        return "";
    }
  }, [locationServiceMapProvider, store]);

  const imageSrc = store.StoreImageUrl;
  const handleSelectCard = () => onSelectCard?.(store.id, store.StoreName, store.storetype ?? "");
  const handleCardKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    handleSelectCard();
  };
  const isInteractive = Boolean(onSelectCard);
  const CardTag = isInteractive ? "div" : "article";

  return (
    <CardTag
      data-testid="storeLocatorCard"
      {...(isInteractive && {
        onClick: handleSelectCard,
        onKeyDown: handleCardKeyDown,
        role: "button",
        tabIndex: 0,
      })}
      className={classNames(styles.resultCard, className, {
        [styles.resultCardActive]: isActive,
        [styles.resultCardFillHeight]: fillHeight,
      })}
    >
      {displayImage && (
        <div className={styles.storeImage}>
          {imageSrc ? (
            <Image
              src={imageSrc}
              alt=""
              fill
              style={{
                objectFit: "cover",
              }}
            />
          ) : (
            <div className={styles.defaultImageWrapper}>
              <StoreDefaultIcon className={styles.defaultImageIcon} />
            </div>
          )}
        </div>
      )}
      <div className={styles.cardHeader}>
        <p className={styles.resultCardShopName}>{store.StoreName}</p>
      </div>
      <div
        className={classNames(styles.cardInfoWrapper, {
          [styles.cardInfoSingleView]: !isListView && !isOpeningHoursBlockVisible,
        })}
      >
        <div
          className={classNames(styles.cardAddressWrapper, {
            [styles.cardAddressList]: isListView,
          })}
        >
          <div className={styles.addressInfo}>
            <span className={styles.address}>{store.address}</span>
            {store.PhoneNumber && <span>{store.PhoneNumber}</span>}
          </div>
          {directionURL && isListView && (
            <StoreLocatorGetDirection isLinkLabel={false} href={directionURL} onClick={onGetDirectionClick} />
          )}
        </div>
        {directionURL && !isListView && <StoreLocatorGetDirection href={directionURL} onClick={onGetDirectionClick} />}
        {isEventCard && <EventDate startDate={eventStore.StartDate} endDate={eventStore.EndDate} />}
        {isOpeningHoursBlockVisible
          ? store.opening_hours?.[0] && (
              <StoreLocatorShopSchedule shopSchedule={store.opening_hours[0]} isListView={isListView ?? false} />
            )
          : store?.Hours && (
              <div>
                <span className={styles.hours}>{__("store_locator.popup.hours_prefix")}</span> {store?.Hours}
              </div>
            )}
        {isAvailableProductsVisible && (
          <StoreLocatorAvailableProducts
            productAttributes={store.product_attributes ?? []}
            isListView={isListView ?? false}
          />
        )}
      </div>
      <div className={styles.cardFooter}>
        {storeDetailsPageUrl && (
          <Button
            variant="secondary"
            href={storeDetailsPageUrl}
            target={storeDetailsPageTarget}
            rel="noreferrer"
            data-testid="getDetailsPageLink"
          >
            {ctaLabel || __("store_locator.popup.store_details")}
          </Button>
        )}
        {children}
      </div>
    </CardTag>
  );
};
